import React, { useState } from 'react';
import { FiX, FiChevronRight } from 'react-icons/fi';
import { SHARED_LIST_TYPES } from '../lib/sharedListTypes';
import './NewSharedListModal.css';

const NewSharedListModal = ({ isOpen, onClose, onCreated, token, boardId = null }) => {
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const reset = () => {
    setStep(1);
    setSelectedType(null);
    setTitle('');
    setDescription('');
    setError('');
    setCreating(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSelectType = (t) => {
    setSelectedType(t);
    setStep(2);
  };

  const handleCreate = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!selectedType) {
      setError('Pick a list type');
      return;
    }
    setCreating(true);
    setError('');
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shared-lists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: title.trim(),
          type: selectedType.value,
          description: description.trim() || null,
          visibility: 'team',
          relatedBoardId: boardId || null,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Failed to create list');
      }
      const d = await r.json();
      reset();
      onCreated && onCreated(d.sharedList);
    } catch (e) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div
        className="modal-content new-shared-list-modal"
        onClick={(e) => e.stopPropagation()}
        data-testid="new-shared-list-modal"
      >
        <div className="modal-header">
          <h2>New Shared List {step === 2 && selectedType ? `— ${selectedType.label}` : ''}</h2>
          <button className="modal-close" onClick={handleClose} aria-label="Close">
            <FiX size={20} />
          </button>
        </div>

        <div className="modal-body">
          {step === 1 && (
            <>
              <p className="new-list-step-hint">Pick a template that matches what you want to track.</p>
              <div className="new-list-type-grid">
                {SHARED_LIST_TYPES.map((t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.value}
                      type="button"
                      className="new-list-type-card"
                      style={{ '--type-color': t.color }}
                      onClick={() => handleSelectType(t)}
                      data-testid={`new-list-type-${t.value}`}
                    >
                      <div className="new-list-type-icon" style={{ background: `${t.color}1A`, color: t.color }}>
                        <Icon size={22} />
                      </div>
                      <div className="new-list-type-body">
                        <div className="new-list-type-label">{t.label}</div>
                        <div className="new-list-type-desc">{t.description}</div>
                      </div>
                      <FiChevronRight size={18} className="new-list-type-chev" />
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {step === 2 && selectedType && (
            <>
              <div className="form-group">
                <label className="form-label">Title</label>
                <input
                  className="input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Apartment 3A — Finishes Snag"
                  data-testid="new-list-title-input"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description (optional)</label>
                <textarea
                  className="input"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What is this list for?"
                  data-testid="new-list-description-input"
                />
              </div>
              {error && <div className="form-error" data-testid="new-list-error">{error}</div>}
            </>
          )}
        </div>

        <div className="modal-footer">
          {step === 2 && (
            <button
              className="btn btn-secondary"
              onClick={() => setStep(1)}
              disabled={creating}
              data-testid="new-list-back-button"
            >
              Back
            </button>
          )}
          <button className="btn btn-secondary" onClick={handleClose} disabled={creating}>
            Cancel
          </button>
          {step === 2 && (
            <button
              className="btn btn-primary"
              onClick={handleCreate}
              disabled={creating || !title.trim()}
              data-testid="new-list-create-button"
            >
              {creating ? 'Creating...' : 'Create List'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewSharedListModal;
